import * as THREE from "three";

function clamp01(value) {
  return Math.min(1, Math.max(0, value));
}

function terrainSlopeHeatColor(normalizedSlope) {
  const color = new THREE.Color();
  // Blue -> green -> yellow -> red, so steep terrain reads warmer.
  color.setHSL((1 - clamp01(normalizedSlope)) * 0.66, 0.95, 0.5);
  return color;
}

function getHeightFieldMetrics(size, hfieldDef) {
  const nrow = Math.max(2, Math.floor(Number(hfieldDef?.nrow) || 2));
  const ncol = Math.max(2, Math.floor(Number(hfieldDef?.ncol) || 2));
  const data = Array.isArray(hfieldDef?.data) ? hfieldDef.data : [];
  const width = 2 * (Number(size?.[0]) || 1);
  const height = 2 * (Number(size?.[1]) || width / 2 || 1);
  const elevation = Number(size?.[2]) || 0.1;
  return { nrow, ncol, data, width, height, elevation };
}

function buildHeightGrid({ nrow, ncol, data, elevation }) {
  const heights = new Array(nrow * ncol);
  for (let row = 0; row < nrow; row += 1) {
    for (let col = 0; col < ncol; col += 1) {
      const index = row * ncol + col;
      heights[index] = (Number(data[index]) || 0) * elevation;
    }
  }
  return heights;
}

/**
 * @param {[number,number,number,number]} size
 * @param {object | null} hfieldDef
 * @param {{ heatmapColorMode?: 'slope' | 'height' }} [options]
 */
export function createHeightFieldGeometry(size, hfieldDef, options = {}) {
  const heatmapColorMode = options.heatmapColorMode === "height" ? "height" : "slope";
  const metrics = getHeightFieldMetrics(size, hfieldDef);
  const { nrow, ncol, data, width, height } = metrics;
  const geometry = new THREE.PlaneGeometry(width, height, ncol - 1, nrow - 1);
  if (data.length >= nrow * ncol) {
    const position = geometry.attributes.position;
    const heights = buildHeightGrid(metrics);
    for (let row = 0; row < nrow; row += 1) {
      for (let col = 0; col < ncol; col += 1) {
        const vertexIndex = row * ncol + col;
        const sampleIndex = row * ncol + col;
        position.setZ(vertexIndex, heights[sampleIndex]);
      }
    }

    const colors = new Float32Array(nrow * ncol * 3);
    const n = nrow * ncol;

    if (heatmapColorMode === "height") {
      let minH = Infinity;
      let maxH = -Infinity;
      for (let i = 0; i < n; i += 1) {
        const h = heights[i];
        if (h < minH) minH = h;
        if (h > maxH) maxH = h;
      }
      const rangeH = maxH - minH;
      for (let index = 0; index < n; index += 1) {
        const color =
          rangeH > 1e-9
            ? terrainSlopeHeatColor((heights[index] - minH) / rangeH)
            : new THREE.Color(1, 1, 1);
        colors[index * 3] = color.r;
        colors[index * 3 + 1] = color.g;
        colors[index * 3 + 2] = color.b;
      }
    } else {
      const cellWidth = width / Math.max(1, ncol - 1);
      const cellHeight = height / Math.max(1, nrow - 1);
      const slopes = new Array(nrow * ncol);
      let maxSlope = 0;
      for (let row = 0; row < nrow; row += 1) {
        for (let col = 0; col < ncol; col += 1) {
          const left = heights[row * ncol + Math.max(0, col - 1)];
          const right = heights[row * ncol + Math.min(ncol - 1, col + 1)];
          const down = heights[Math.max(0, row - 1) * ncol + col];
          const up = heights[Math.min(nrow - 1, row + 1) * ncol + col];
          const dx = (right - left) / (cellWidth * (col === 0 || col === ncol - 1 ? 1 : 2));
          const dy = (up - down) / (cellHeight * (row === 0 || row === nrow - 1 ? 1 : 2));
          const slope = Math.sqrt(dx * dx + dy * dy);
          slopes[row * ncol + col] = slope;
          maxSlope = Math.max(maxSlope, slope);
        }
      }

      for (let index = 0; index < slopes.length; index += 1) {
        const color =
          maxSlope > 1e-9
            ? terrainSlopeHeatColor(slopes[index] / maxSlope)
            : new THREE.Color(1, 1, 1);
        colors[index * 3] = color.r;
        colors[index * 3 + 1] = color.g;
        colors[index * 3 + 2] = color.b;
      }
    }

    geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    position.needsUpdate = true;
    geometry.computeVertexNormals();
  }
  return geometry;
}

function getContourPoint(start, end, level) {
  const t = (level - start.z) / (end.z - start.z);
  return {
    x: start.x + (end.x - start.x) * t,
    y: start.y + (end.y - start.y) * t,
    z: level + 0.006,
  };
}

function sampleHeightBilinear(heights, nrow, ncol, width, heightPlane, x, y, zLift = 0.012) {
  const maxCol = ncol - 1;
  const maxRow = nrow - 1;
  if (maxCol <= 0 || maxRow <= 0) {
    return (heights[0] || 0) + zLift;
  }
  let col_f = ((x + width / 2) / width) * maxCol;
  let row_f = ((heightPlane / 2 - y) / heightPlane) * maxRow;
  col_f = Math.max(0, Math.min(maxCol, col_f));
  row_f = Math.max(0, Math.min(maxRow, row_f));
  const c0 = Math.floor(col_f);
  const r0 = Math.floor(row_f);
  const c1 = Math.min(maxCol, c0 + 1);
  const r1 = Math.min(maxRow, r0 + 1);
  const dc = col_f - c0;
  const dr = row_f - r0;
  const z00 = heights[r0 * ncol + c0];
  const z10 = heights[r0 * ncol + c1];
  const z01 = heights[r1 * ncol + c0];
  const z11 = heights[r1 * ncol + c1];
  const z0 = z00 * (1 - dc) + z10 * dc;
  const z1 = z01 * (1 - dc) + z11 * dc;
  return z0 * (1 - dr) + z1 * dr + zLift;
}

function collectContourIntersections(corners, level) {
  const edges = [
    [corners[0], corners[1]],
    [corners[1], corners[2]],
    [corners[2], corners[3]],
    [corners[3], corners[0]],
  ];
  const intersections = [];
  edges.forEach(([start, end]) => {
    const min = Math.min(start.z, end.z);
    const max = Math.max(start.z, end.z);
    if (max - min <= 1e-9 || level < min || level >= max) return;
    intersections.push(getContourPoint(start, end, level));
  });
  return intersections;
}

export function createHeightFieldContourLines(size, hfieldDef, contourCount = 12) {
  const metrics = getHeightFieldMetrics(size, hfieldDef);
  const { nrow, ncol, data, width, height } = metrics;
  if (data.length < nrow * ncol) return null;

  const heights = buildHeightGrid(metrics);
  const minHeight = Math.min(...heights);
  const maxHeight = Math.max(...heights);
  if (maxHeight - minHeight <= 1e-9) return null;

  const cellWidth = width / Math.max(1, ncol - 1);
  const cellHeight = height / Math.max(1, nrow - 1);
  const positions = [];
  const levelStep = (maxHeight - minHeight) / (contourCount + 1);

  for (let levelIndex = 1; levelIndex <= contourCount; levelIndex += 1) {
    const level = minHeight + levelStep * levelIndex;
    for (let row = 0; row < nrow - 1; row += 1) {
      for (let col = 0; col < ncol - 1; col += 1) {
        const x0 = -width / 2 + col * cellWidth;
        const x1 = x0 + cellWidth;
        const y0 = height / 2 - row * cellHeight;
        const y1 = y0 - cellHeight;
        const corners = [
          { x: x0, y: y0, z: heights[row * ncol + col] },
          { x: x1, y: y0, z: heights[row * ncol + col + 1] },
          { x: x1, y: y1, z: heights[(row + 1) * ncol + col + 1] },
          { x: x0, y: y1, z: heights[(row + 1) * ncol + col] },
        ];
        const intersections = collectContourIntersections(corners, level);
        for (let i = 0; i + 1 < intersections.length; i += 2) {
          const start = intersections[i];
          const end = intersections[i + 1];
          positions.push(start.x, start.y, start.z, end.x, end.y, end.z);
        }
      }
    }
  }

  if (positions.length === 0) return null;
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(new Float32Array(positions), 3));
  const material = new THREE.LineBasicMaterial({
    color: 0x111827,
    transparent: true,
    opacity: 0.75,
    depthTest: true,
  });
  const contours = new THREE.LineSegments(geometry, material);
  contours.name = "sceneFloorContours";
  contours.renderOrder = 2;
  return contours;
}

/**
 * Planar grid lines (aligned to X / Y in floor local space) lifted onto the heightfield—like GridHelper, but bent by slopes.
 */
export function createHeightFieldSurfaceGrid(
  size,
  hfieldDef,
  divisions = 20,
  samplesPerSegment = 72
) {
  const metrics = getHeightFieldMetrics(size, hfieldDef);
  const { nrow, ncol, data, width, height: heightPlane } = metrics;
  if (data.length < nrow * ncol) return null;

  const heights = buildHeightGrid(metrics);
  const positions = [];
  const xMin = -width / 2;
  const xMax = width / 2;
  const yMin = -heightPlane / 2;
  const yMax = heightPlane / 2;

  const pushLineConstX = (xFixed) => {
    const n = Math.max(2, samplesPerSegment);
    for (let s = 0; s < n; s += 1) {
      const t0 = s / n;
      const t1 = (s + 1) / n;
      const y0 = yMin + (yMax - yMin) * t0;
      const y1 = yMin + (yMax - yMin) * t1;
      const z0 = sampleHeightBilinear(heights, nrow, ncol, width, heightPlane, xFixed, y0);
      const z1 = sampleHeightBilinear(heights, nrow, ncol, width, heightPlane, xFixed, y1);
      positions.push(xFixed, y0, z0, xFixed, y1, z1);
    }
  };

  const pushLineConstY = (yFixed) => {
    const n = Math.max(2, samplesPerSegment);
    for (let s = 0; s < n; s += 1) {
      const t0 = s / n;
      const t1 = (s + 1) / n;
      const x0 = xMin + (xMax - xMin) * t0;
      const x1 = xMin + (xMax - xMin) * t1;
      const z0 = sampleHeightBilinear(heights, nrow, ncol, width, heightPlane, x0, yFixed);
      const z1 = sampleHeightBilinear(heights, nrow, ncol, width, heightPlane, x1, yFixed);
      positions.push(x0, yFixed, z0, x1, yFixed, z1);
    }
  };

  const div = Math.max(1, Math.floor(divisions));
  for (let i = 0; i <= div; i += 1) {
    const f = i / div;
    pushLineConstX(xMin + f * (xMax - xMin));
    pushLineConstY(yMin + f * (yMax - yMin));
  }

  if (positions.length === 0) return null;
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(new Float32Array(positions), 3));
  const material = new THREE.LineBasicMaterial({
    color: 0xaaaaaa,
    transparent: true,
    opacity: 0.6,
    depthTest: true,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -0.5,
    polygonOffsetUnits: -0.5,
  });
  const grid = new THREE.LineSegments(geometry, material);
  grid.name = "sceneFloorSurfaceGrid";
  grid.renderOrder = 1;
  return grid;
}

/** Flat square grid in floor local XY at constant Z (default plane before terrain). */
export function createFlatFloorSurfaceGrid(
  floorSize,
  divisions = 20,
  samplesPerSegment = 72,
  zLift = 0.018
) {
  const width = Math.max(0.25, Number(floorSize) || 20);
  const heightPlane = width;
  const positions = [];
  const xMin = -width / 2;
  const xMax = width / 2;
  const yMin = -heightPlane / 2;
  const yMax = heightPlane / 2;
  const n = Math.max(2, samplesPerSegment);

  const pushLineConstX = (xFixed) => {
    for (let s = 0; s < n; s += 1) {
      const t0 = s / n;
      const t1 = (s + 1) / n;
      const y0 = yMin + (yMax - yMin) * t0;
      const y1 = yMin + (yMax - yMin) * t1;
      positions.push(xFixed, y0, zLift, xFixed, y1, zLift);
    }
  };

  const pushLineConstY = (yFixed) => {
    for (let s = 0; s < n; s += 1) {
      const t0 = s / n;
      const t1 = (s + 1) / n;
      const x0 = xMin + (xMax - xMin) * t0;
      const x1 = xMin + (xMax - xMin) * t1;
      positions.push(x0, yFixed, zLift, x1, yFixed, zLift);
    }
  };

  const div = Math.max(1, Math.floor(divisions));
  for (let i = 0; i <= div; i += 1) {
    const f = i / div;
    pushLineConstX(xMin + f * (xMax - xMin));
    pushLineConstY(yMin + f * (yMax - yMin));
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(new Float32Array(positions), 3));
  const material = new THREE.LineBasicMaterial({
    color: 0xaaaaaa,
    transparent: true,
    opacity: 0.6,
    depthTest: true,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -0.5,
    polygonOffsetUnits: -0.5,
  });
  const grid = new THREE.LineSegments(geometry, material);
  grid.name = "sceneFloorSurfaceGrid";
  grid.renderOrder = 1;
  return grid;
}
